/**
 * Theme Resolver
 *
 * Maps BrandTokens → partial overrides for every affected screen/component theme.
 * The result is shallow-merged on top of the base screenTheme, so only
 * brand-colored properties change — structural layout is untouched.
 *
 * Cache key = `<themeId>:<mode>` so overrides are computed once per theme+mode combo.
 *
 * DARK MODE STRATEGY
 * ──────────────────
 * Interactive / brand elements (buttons, active tabs, badges, shift banner,
 * WO-number links, CTAs) → always use brand tokens regardless of mode.
 *
 * Surface / background elements (page canvas, sidebars, cards, list headers,
 * borders) → in dark mode, swap out the light-tint palette values for the
 * dark palette values from palette.colors.ts so dark surfaces don't become
 * light-blue tints.
 */
import type { BrandTokens } from './types';
import { palette } from '../colorGroups/palette.colors';

/** Partial override bag keyed by screen name from screenThemes */
export type ClientThemeOverrides = Record<string, Record<string, string>>;

const _cache = new Map<string, ClientThemeOverrides>();

// ─── Dark-mode surface palette ────────────────────────────────────────────────
// These replace the light-tint tokens when mode === 'dark'.
const darkSurfaces = {
  /** Main page canvas */
  page:           palette.blue.onTint,    // '#181920'
  /** Sidebar, modals, card backgrounds */
  content:        palette.dark.background,       // '#1E1F27'
  /** Tile / WO cards */
  card:           palette.blue.softBlue,   // '#001229'
  /** List-view header row */
  listHeader:     palette.blue.pressed,       // '#252630'
  /** Generic border / separator */
  border:         palette.gray.darkGreyLight,        // '#3A4B61'
  /** Selected-row / selected-tab fill (dark navy tint, replaces primaryTint) */
  selectedFill:   '#26364A',
  /** Secondary selected fill (slightly deeper, replaces primaryTintMed) */
  selectedFillMed:'#162847',
  /** Text on dark surfaces */
  text:           palette.gray.white,          // '#FFFFFF'
  activeLabel: palette.gray.white,          // '#FFFFFF'
  menuBorder: '#26364A',        // '#26364A'
  secondaryText: palette.gray.white,          // '#FFFFFF'
  CTABorder: palette.gray.placeholderText,        // '#3A4B61'
  onPrimary: palette.dark.background,          // '#181920',
  secondaryBorderColor: palette.gray.placeholderText,   
  workOrderTileViewIconBorder: palette.gray.darkGreyLight,
  workOrderFilterTextBorder: palette.gray.white,
  statusText: palette.gray.white,
  workOrderIdValueColor: palette.gray.white,
  pinAuthIconBGColor: palette.blue.pressed,
};

/**
 * Resolve brand tokens into per-screen property overrides.
 *
 * @param tokens    Brand token set for the active client theme
 * @param cacheKey  `<themeId>:<mode>` — uniquely identifies this combination
 * @param mode      'light' | 'dark' — controls surface vs. brand color selection
 *
 * Brand property → screen theme key mapping (from Figma spec):
 *
 *  primary       → button BG, active-tab bg, shift banner, icon-toggle active,
 *                  selected pagination, leaderboard self-card, WO-number links
 *  primaryHover  → (internal hover — used by CSS/web hover rules)
 *  primaryBadge  → active tab badge fill (the count badge inside the active tab)
 *  primaryTint   → inactive-tab badge fill, selected sidebar tab bg, selected row bg
 *  primaryBorder → outlined/secondary button border, sidebar selected border,
 *                  pagination button outline
 *  onPrimary     → text/icons on solid primary fills
 *  onTint        → text on tint/outlined areas
 */
export function resolveClientOverrides(
  tokens: BrandTokens,
  cacheKey: string,
  mode: 'light' | 'dark' = 'light',
): ClientThemeOverrides {
  if (_cache.has(cacheKey)) {
    return _cache.get(cacheKey)!;
  }

  const isDark = mode === 'dark';

  // Surface helpers — mode-aware
  /** Page-level canvas background */
  const surfacePage        = isDark ? tokens.onTint            : tokens.pageBackground;
  /** Sidebar / card / modal background */
  const surfaceContent     = isDark ? darkSurfaces.content     : tokens.onPrimary;
  /** Tile / WO card background */
  const surfaceCard        = isDark ? darkSurfaces.card        : tokens.onPrimary;
  /** List header row background */
  const surfaceListHeader  = isDark ? tokens.primaryPressed     : tokens.primaryTint;
  /** Generic border / separator color */
  const surfaceBorder      = isDark ? darkSurfaces.border      : tokens.primaryTintMed;
  const meterCard = isDark ? palette.gray.darkGreyLight      : tokens.primaryTintMed;
  const footerBorder = isDark ? palette.gray.lightDarkGrey      : tokens.primaryTintMed;
  const cardBorderColor = isDark ? palette.gray.lightDarkGrey : tokens.primaryTintMed;
  const arrowIcon = isDark ? darkSurfaces.border     : tokens.primaryBorder;
  const arrowInnerIcon = isDark ? palette.gray.white     : tokens.onTint;
  const arrowExpandedInnerIcon = isDark ? palette.gray.white     : palette.gray.black;
  const addPartsNoteBorder= isDark  ? tokens.primaryBadge       : tokens.primaryTintMed;
  /** Selected tab / row fill (light tint in light, dark navy in dark) */
  const surfaceSelectedHeaderFill    = isDark ? palette.gray.lightDarkGrey   : tokens.primaryTint;
  const surfaceSelectedFill    = isDark ? palette.gray.searchResult   : tokens.primaryTint;
  const addPartsNoteBg = isDark ? tokens.primaryPressed   : tokens.primaryTint;
  const formInputBorder = isDark ? palette.gray.lightDarkGrey   : tokens.primaryTint;
  const partsViewBorder = isDark ? palette.gray.lightDarkGrey   : tokens.primaryTint;
  const scoreBoardCardBorder   = isDark ? palette.blue.onTint   : tokens.primaryTint;
  const infoStrip = isDark ? palette.gray.lightDarkGrey   : tokens.primaryTint;
  const selectedtabModalBGColor = isDark ? tokens.primaryPressed   : tokens.primaryTint;
  const listeningEnableIconBG = isDark ? palette.gray.lightDarkGrey : tokens.primaryTint;
  const noteSectionSelectedBorder = isDark ? tokens.primary : tokens.primary;
  const noteSectionSelectedBG = isDark ? tokens.primaryDark : tokens.primaryTint;
  const notesFilterUnselectBGColor = isDark ? darkSurfaces.pinAuthIconBGColor : tokens.primaryTint;
  /** Deeper selected fill (used for pressed / hover states) */
  const surfaceSelectedFillMed = isDark ? darkSurfaces.selectedFillMed : tokens.primaryTintMed;
  /** Text on list/table surfaces (dark in light mode, muted light in dark) */
  const surfaceHeaderText  = isDark ? tokens.onPrimary         : tokens.onTint;
  const dropdownSelectedFill = isDark ? tokens.primaryPressed  : tokens.primaryTint;
  const dropdownSelectedFillText = isDark ? tokens.onPrimary   : tokens.primary;
  const tabContainerBG = isDark ? 'transparent' : tokens.onPrimary;
  const repairShiftSectionBGColor = isDark ? tokens.primaryPressed  : tokens.primaryTint;
  const shiftBGColor = isDark ? palette.blue.shiftColor : tokens.primary

  const overrides: ClientThemeOverrides = {
    // ─── Agency Selection / Login screens ────────────────────────────────────
    loginScreen: {
      /** Page background — lightest brand tint canvas (matches main layout BG) */
      mainBackgroundColor: surfacePage,
      /** Card border — medium tint (#CCE3FF) matches outlined-button border */
      borderColor: surfaceBorder,
    },

    // ─── Button ───────────────────────────────────────────────────────────────
    button: {
      primaryBG: tokens.primary,
      primartText: tokens.onPrimary,
      primaryHoverBG: tokens.primaryHover,
      primaryPressedBG: tokens.primaryTintMed,
      bGEnableColor: tokens.primary,
      textEnableColor: tokens.onPrimary,
      borderColor: tokens.primaryBorder,
      /** Secondary outline border = brand primaryBorder (Figma: outlined/secondary button border) */
      secondaryBorderColor: mode === 'dark' ? darkSurfaces.secondaryBorderColor: tokens.primaryBorder,
      /** Secondary button bg = white fill (onPrimary = #FFFFFF).
       *  Declared here as a runtime override so it is never stale from the
       *  module-level screenThemes initialisation. */
      secondaryBG: mode === 'dark' ? 'transparent' : tokens.onPrimary,
      /** Secondary button text = onTint (Figma: #020617 — dark text on transparent/tint bg) */
      secondaryText: mode === 'dark' ?  darkSurfaces.secondaryText: tokens.onTint,
      /** Secondary button hover fill — light tint in light mode, primary-hover fill in dark mode */
      secondaryHoverBG: mode === 'dark' ? tokens.primary : tokens.primaryTint,
      /** Secondary button pressed fill — medium tint in light mode, primary-pressed fill in dark mode */
      secondaryPressedBG: mode === 'dark' ? tokens.primaryPressed : tokens.primaryTintMed,
      // /** Disabled button background — lightest brand tint so disabled state is
      //  *  on-brand but clearly muted (e.g. #CCE3FF for blue, #FFCDD2 for red).
      //  *  Overrides the static gray in screenThemes so every brand gets its own
      //  *  disabled colour rather than sharing a generic grey-blue. */
      // bGDisableColor: tokens.primaryTintMed,
      // /** Disabled button text — white on the brand-tinted disabled BG,
      //  *  matching the enabled button's onPrimary text colour (#FFFFFF). */
      // textDisableColor: tokens.onPrimary,
      subTitleBadgeBg: tokens.primaryBadge,
      /** Badge hover bg — in dark mode use primaryBadge (#004EA8) so:
       *  • selected-tab badge stays at #004EA8 on hover (no visible change)
       *  • non-selected-tab badge turns #004EA8 on hover (visible, distinct from container hover #005FCC)
       *  Light mode keeps #FFFFFF (white — matches existing behaviour). */
      subTitleBadgeText: isDark ? tokens.primaryBadge : tokens.onPrimary,
    },

    // ─── Header ───────────────────────────────────────────────────────────────
    header: {
      // Shift status badge background ("Status: Job Assignment")
      shiftStatus: surfaceSelectedHeaderFill,
      statusText: isDark ? darkSurfaces.statusText : tokens.primary,
      // CTAs in header that use primary fill
      CTAEnabledBG: tokens.primary,
      CTAEnabledBGText: tokens.onPrimary,
      IndirectActivityCTABorder: mode === 'dark' ? darkSurfaces.CTABorder : tokens.primaryBorder,
      IndirectActivityCTAText: tokens.primary,
      /** Indirect Activity icon color when button is disabled (#FFFFFF) */
      headerIndirectActivityDisabledIconColor: tokens.onPrimary,
      border: isDark ? darkSurfaces.menuBorder : 'transparent',
    },

    // ─── Profile component ────────────────────────────────────────────────────
    profile: {
      // Avatar circle background and text use primary brand colour
      BGColor: tokens.primary,
      textColor: tokens.onPrimary,
    },

    // ─── Nova AI chat bubble ──────────────────────────────────────────────────
    novaAIScreen: {
      // Floating Nova AI toggle button background
      novaAIIconBGColor: tokens.primary,
      botIconBGColor: tokens.primary,
      /** ChatBot icon background — brand tint in light, deep brand pressed in dark */
      chatBotIconBGColor: isDark ? palette.blue.pressed : tokens.primaryTint,
      /** ChatBot icon stroke — brand primary in both modes */
      chatBotIconColor: tokens.primary,
    },

    // ─── Dashboard screen ─────────────────────────────────────────────────────
    DashboardScreen: {
      // Page background — app-level lightest canvas (matches main layout BG)
      background: surfacePage,

      // Shift banner header background — always brand primary
      shiftBGColor: shiftBGColor,

      QABannerBG: mode === 'dark' ? tokens.primary: tokens.primaryTint,

      // Work-order card default border
      tileViewBorder: surfaceSelectedFill,
      shiftSummaryBorder: surfaceBorder,
      partsViewBorder: partsViewBorder,

      // Work-order card hover/selected border — brand primary in both modes
      tileViewSelectedBorder: tokens.primary,

      // Work-order card background
      listViewBG: surfaceCard,

      // Main Repairs / Parts Inventory tab — active fill + inactive underline
      workOrderFilterSelectedTextBorder: tokens.primary,
      workOrderFilterTextBorder: isDark ? darkSurfaces.workOrderFilterTextBorder : tokens.primary,
      // Outer rounded container
      tabContainerBG: tabContainerBG,
      tabContainerBorder: surfaceBorder,

      // Filter tabs — active state ("All" pill) — brand primary always
      selectedCountBG: tokens.primary,
      selectedCountTextColor: tokens.onPrimary,
      tabIconSelectedColor: tokens.onPrimary,
      tabTextSelectedColor: tokens.onPrimary,

      // Filter tabs — inactive state badge
      countBG: isDark ? darkSurfaces.selectedFillMed : tokens.primaryTintMed,
      countTextColor: tokens.primary,

      // Prev / Next pagination button text
      listPaginationButtonText: tokens.primaryBorder,

      // WO Number / Repair ID link text — brand primary always
      repairIdValueColor: tokens.primary,
      workOrderIdValueColor: isDark ? darkSurfaces.workOrderIdValueColor: tokens.primary,
      repairNumber: tokens.primary,

      // Tile/List view toggle icons — active: primary BG, white icon
      workOrderTileViewIcon: tokens.primary,
      workOrderTileViewSelectedIcon: tokens.onPrimary,
      workOrderTileViewIconBorder: isDark ? darkSurfaces.workOrderTileViewIconBorder : tokens.primaryTintMed,

      // Card notification icon colors
      cardNotificationIconColor: tokens.primary,
      cardNotificationBGColor: surfaceSelectedFill,

      // Notification badge — brand primary always
      notificationCountBGColor: tokens.primary,
      headerCalenderBG: tokens.primary,
      notificationCountTextColor: tokens.onPrimary,

      // List view table: header row BG, data row BG, borders, separators
      listViewHeaderBG: surfaceListHeader,
      listViewRowBG: surfaceCard,
      listViewBorder: surfaceBorder,
      separatorColor: surfaceBorder,
      listViewHeaderText: surfaceHeaderText,

      // Leaderboard self-card — brand primary always
      leaderBoardCardSelfLeaderBG: tokens.primary,
      leaderBoardCardSelfLeaderName: tokens.onPrimary,
      repairBoardCardBorder: scoreBoardCardBorder,
      subTitleBadgeBg: tokens.primaryBadge,
      sortIconColor: isDark ? darkSurfaces.text : tokens.onTint,
      selectedSortIconColor: isDark ? tokens.primaryBorder: tokens.primary,
      selectedTab: surfaceSelectedHeaderFill,
      selectedTabBorder: isDark ? 'transparent' : tokens.primary,
    },

    // ─── Sidebar / Main layout ────────────────────────────────────────────────
    MainLayoutScreen: {
      // Page-level background
      background: surfacePage,
      // Sidebar / menu background
      sidebarBG: isDark ? tokens.onTint : tokens.onPrimary,
      // Selected nav item — active tab fill + outline
      selectedTab: surfaceSelectedHeaderFill,
      selectedTabBorder: isDark ? 'transparent' : tokens.primary,
      // Active icon / label colour — brand primary always
      activeLabel: isDark ? darkSurfaces.activeLabel : tokens.primary,
      // Hover fill
      tabHoverBg: surfaceSelectedFill,
      // Pressed fill
      tabPressedBg: surfaceSelectedFillMed,
      /** Disabled menu-item background */
      tabDisabledBg: palette.gray.tabDisabled,
      /** Disabled menu-item label text */
      tabDisabledText: palette.gray.disabledCTAText,
      /** Disabled menu-item icon colour */
      tabDisabledIconColor: palette.gray.disabledCTAText,
      /** Sidebar right border — visible in dark mode, transparent in light mode */
      border: isDark ? darkSurfaces.menuBorder : 'transparent',
    },

    // ─── Pagination ───────────────────────────────────────────────────────────
    pagination: {
      /** Active page button fill — brand primary always */
      selectedPagination: tokens.primary,
      /** Active page number text (#FFFFFF) */
      selectedPaginationText: tokens.onPrimary,
      /** Prev/Next button text + inactive page number text */
      listPaginationButtonText: tokens.primaryBorder,
      /** Page button border + Prev/Next button border */
      listPaginationBorder: surfaceBorder,
      /** Prev/Next + inactive page button background */
      paginationButtonBG: isDark ? 'transparent' : tokens.onPrimary,
      /** Prev/Next + inactive page number text */
      paginationButtonText: isDark ? darkSurfaces.text : tokens.onTint,
    },

    // ─── Shop home page ───────────────────────────────────────────────────────
    shopHomePage: {
      // Page background
      mainBackgroundColor: surfacePage,
      // Content container / filter border
      borderColor: surfaceBorder,
      cardBorderColor: cardBorderColor,
      listPaginationButtonText: tokens.primaryBorder,
      // Tile/List view toggle icons — active: primary BG, white icon
      workOrderTileViewIcon: tokens.primary,
      workOrderTileViewSelectedIcon: tokens.onPrimary,
      // List view table: header row BG, data row BG, borders and separators
      listViewHeaderBG: surfaceListHeader,
      listViewRowBG: surfaceCard,
      listViewBorder: surfaceBorder,
      separatorColor: surfaceBorder,
      listViewHeaderText: surfaceHeaderText,
      shopHomePageMessageIcon: surfaceSelectedFill,
      workOrderIdValueColor: isDark ? darkSurfaces.workOrderIdValueColor: tokens.primary,
      shiftStatus: surfaceSelectedHeaderFill,
      footerBorder: footerBorder,
    },

    pinAuthentication: {
      pinAuthIconBGColor: isDark ? darkSurfaces.pinAuthIconBGColor : tokens.primaryTint,
      pinAuthBorderColor: isDark ? darkSurfaces.workOrderIdValueColor: tokens.primary,
      /** Focused PIN input border — brand primary in both modes (matches inputText.activeBorderColor).
       *  Overrides the static '#66ABFF' (palette.blue.selectedBorder) which appears as
       *  near-white on dark modal backgrounds. */
      pinInputTextBorder: tokens.primary,
    },

    cards: {
      scoreBoardCardBorder: scoreBoardCardBorder,
    },

    // ─── Repair Details ───────────────────────────────────────────────────────
    RepairDetailsScreen: {
      // Page background
      background: surfacePage,
      tabsSelectedColor: isDark ? darkSurfaces.text : tokens.primary,
      listeningDisableIconBG: isDark ? tokens.primaryPressed : tokens.primary,
      tabsSelectedSubColor: isDark ? tokens.primaryTint : tokens.primary,
      mainTabsSelectedColor: tokens.primary,
      selectedtabBGColor: surfaceSelectedFill,
      infoStrip: infoStrip,
      selectedtabModalBGColor: selectedtabModalBGColor,
      selectedtabBorder: isDark ? palette.gray.placeholderText: tokens.primarySoft,
      infoStripBorder: isDark ? 'transparent': tokens.primarySoft,
      selectedCountBG: tokens.primary,
      saveCTABGColor: tokens.primary,
    saveCTATitleColor: tokens.onPrimary,
      saveCTAIconColor: tokens.onPrimary,
      // Tile/List view toggle icons — active: primary BG, white icon
      workOrderTileViewIcon: tokens.primary,
      workOrderTileViewSelectedIcon: tokens.onPrimary,
      // Main tab pill container
      tabContainerBG: isDark ? darkSurfaces.content : tokens.onPrimary,
      tabContainerBorder: surfaceBorder,
      // Inactive tab text — brand primary always
      tabsTextColor: isDark ? darkSurfaces.text : tokens.primary,
      // Active tab text — white on solid primary
      tabsSelectedTextColor: tokens.onPrimary,
      maintabSelectedTextColor: tokens.onPrimary,
      // RepairShift section backgrounds
      repairShiftSectionBGColor: repairShiftSectionBGColor,
      repairShiftMainSectionBGColor: surfacePage,
      notesBorder: surfaceBorder,
      notesFilterUnselectBGColor: notesFilterUnselectBGColor,
      viewNoteInfoBorder: surfaceBorder,
      noteSectionSelectedBG: noteSectionSelectedBG,
      noteSectionBorder: surfaceBorder,
      meterCard: meterCard,
      listeningEnableIconBG: listeningEnableIconBG,
      listeningEnabledCTABG: surfaceSelectedFill,
      noteSectionApplySelectedBG: tokens.primary,
      listViewHeaderBG: surfaceListHeader,
      listViewRowBG: surfaceCard,
      listViewBorder: surfaceBorder,
      separatorColor: surfaceBorder,
      listViewHeaderText: surfaceHeaderText,
      listViewSelectedRowBG: isDark ? 'transparent' : tokens.pageBackground,
      addPartsNoteBg: addPartsNoteBg,
      addPartsNoteBorder: addPartsNoteBorder,
      ownPartsBorder: surfaceSelectedFill,
      noteSectionSelectedBorder: noteSectionSelectedBorder,
      noteHeaderSeparator: surfaceSelectedFill,
      subTitleBadgeBg: tokens.primaryBadge,
      /** Tasks tab outer button BG when active — always brand primary */
      tasksTabActiveBG: tokens.primary,
      /** Tasks tab badge/counter BG when active.
       *  Dark: brand badge fill (#004EA8). Light: white (preserves existing light-mode look). */
      tasksTabBadgeBG: isDark ? tokens.primaryBadge : tokens.onPrimary,
      inputBorder: surfaceBorder,
      tabTextSelectedColor: tokens.onPrimary,
      workOrderFilterSelectedTextBorder: tokens.primary,
      filterButtonText: isDark ? palette.gray.white: tokens.primary,
      /** Grouped dropdown panel open border */
      openBorderColor: tokens.primaryBorder,
      /** Grouped dropdown selected item background */
      selectedDropdownBGColor: dropdownSelectedFill,
      /** Grouped dropdown selected/expanded text color */
      selectedDropdownTextColor: dropdownSelectedFillText,
      /** Separator border between groups inside the grouped dropdown panel */
      groupedDropdownSeparatorColor: surfaceSelectedFill,
      sortIconColor: isDark ? darkSurfaces.text : tokens.onTint,
      selectedSortIconColor: isDark ? tokens.primaryBorder: tokens.primary,
      disabledText: palette.gray.tabDisabledText,
      readyBackground: isDark ? tokens.primaryPressed: palette.backgrounds.lightBlue,
      workOrderTileViewIconBorder: isDark ? darkSurfaces.workOrderTileViewIconBorder : tokens.primaryTintMed,
    },

    // ─── Create Work Order flow ───────────────────────────────────────────────
    createWorkOrderFlow: {
      // Page background
      pageBackground: surfacePage,
      primaryBG: tokens.primary,
      verifyBtnEnabledBG: tokens.primary,
      verifyBtnEnabledText: tokens.onPrimary,
      nextBtnEnabledBG: tokens.primary,
      nextBtnEnabledText: tokens.onPrimary,
      selectedCTA: tokens.primary,
      selectCTAText: tokens.onPrimary,
      // Tile/List view toggle icons — active: primary BG, white icon
      workOrderTileViewIcon: tokens.primary,
      workOrderTileViewSelectedIcon: tokens.onPrimary,
      footerBorder: footerBorder,
      formInputBorder: formInputBorder,
      listViewBorder: cardBorderColor,
      arrowIcon: arrowIcon,
      arrowInnerIcon: arrowInnerIcon,
      arrowExpandedInnerIcon: arrowExpandedInnerIcon,
      assetDetailsDivider: surfaceBorder,
      listViewHeaderBG: surfaceListHeader,
      // Filter tabs — inactive state badge
      countBG: isDark ? darkSurfaces.selectedFillMed : tokens.primaryTintMed,
      countTextColor: tokens.primary,
      workOrderStatusSelectedTextBG: tokens.primary,
      /** Inactive tab text — white in dark mode so it stays legible on hover (primary-fill BG).
       *  Matches RepairDetailsScreen tabsTextColor: isDark ? '#FFFFFF' : tokens.primary. */
      tabTextColor: isDark ? tokens.onPrimary : tokens.primary,
      warrantyboxBorder: isDark ? palette.gray.lightDarkGrey  : tokens.primaryTint,
      workOrderIdValueColor: isDark ? tokens.primaryBorder : tokens.primary,
      workOrderTileViewIconBorder: isDark ? darkSurfaces.workOrderTileViewIconBorder : tokens.primaryTintMed,
      // ── Contact-section tab tokens (mirrors RepairDetailsScreen tasksTab* pattern) ──
      /** Active tab pill background — brand primary, always */
      contactTabActiveBG: tokens.primary,
      /** Active tab label text — white on solid primary */
      contactTabActiveText: tokens.onPrimary,
      /** Active count badge fill.
       *  Dark: brand badge fill (primaryBadge).  Light: white (matches tasksTabBadgeBG light). */
      contactTabActiveBadgeBG: isDark ? tokens.primaryBadge : tokens.onPrimary,
      /** Active count badge text colour.
       *  Dark: white on dark badge.  Light: brand primary on white badge. */
      contactTabActiveBadgeText: isDark ? tokens.onPrimary : tokens.primary,
    },

    stepper: {
      activeCircleBorder: isDark ? tokens.primary : tokens.primary,
      activeCircleBG: isDark ? tokens.primaryPressed: 'transparent',
      completedCircleBG: isDark ? tokens.primaryPressed : tokens.primary,
      activeCircleText: isDark ? tokens.primarySoft : tokens.primary,
      connectorActive: tokens.primary
    },

    // ─── Search Input ─────────────────────────────────────────────────────────
    searchInput: {
      /** Default border */
      borderColor: surfaceBorder,
      /** Hover border = primaryBorder — interactive, same in both modes */
      hoverBorderColor: tokens.primaryBorder,
      /** Focus / Active border = primary — interactive, same in both modes */
      activeBorderColor: tokens.primary,
      iconColor: surfaceBorder,
      searchResultBGColor: surfaceSelectedFill,
    },

    // ─── Dropdown ─────────────────────────────────────────────────────────────
    dropdown: {
      /** Default trigger border */
      workOrderSortIconsBorder: surfaceBorder,
      /** Trigger button hover background — brand hover fill in dark, light tint in light */
      hoverBGColor: isDark ? tokens.primaryHover : surfaceSelectedFill,
      /** Item hover background — brand hover fill in dark, light tint in light */
      menuItemHoverBGColor: isDark ? tokens.primaryHover : surfaceSelectedFill,
      /** Open/active border — interactive, same in both modes */
      openBorderColor: tokens.primaryBorder,
      selectedDropdownBGColor: dropdownSelectedFill,
      selectedDropdownTextColor: dropdownSelectedFillText,
      searchResultBGColor: isDark ? tokens.primaryHover : surfaceSelectedFill,
      searchBorder: tokens.primaryBorder,
    },

    // ─── Checkbox ─────────────────────────────────────────────────────────────
    checkbox: {
      // ── Normal (unchecked) ──────────────────────────────────────────────
      /** Normal border = primaryBorder — interactive, same in both modes */
      listUnSelectedBorder: tokens.primaryBorder,

      // ── Checked ─────────────────────────────────────────────────────────
      /** Checked fill = brand primary — always */
      listSelectedBG: tokens.primary,

      // ── Hover (unchecked) ───────────────────────────────────────────────
      /** Hover fill — surface-aware */
      hoverBG: surfaceSelectedFill,
      /** Hover border = brand primaryBorder — interactive */
      hoverBorder: tokens.primaryBorder,

      // ── Pressed (unchecked) ─────────────────────────────────────────────
      /** Pressed fill — surface-aware */
      pressedBG: surfaceSelectedFillMed,
      /** Pressed border = brand primaryBorder — interactive */
      pressedBorder: tokens.primaryBorder,

      // ── borderColor (generic override) ──────────────────────────────────
      borderColor: tokens.primaryBorder,

      // Disabled states are brand-neutral — not overridden here.
    },

    // ─── Text Input ───────────────────────────────────────────────────────────
    inputText: {
      /** Default border */
      inputBorder: surfaceBorder,
      /** Hover border = primaryBorder — interactive, same in both modes */
      hoverBorderColor: tokens.primaryBorder,
      /** Focus / Active border = primary — interactive, same in both modes */
      activeBorderColor: tokens.primary,
    },
  };

  _cache.set(cacheKey, overrides);
  return overrides;
}

/**
 * Shallow-merge brand overrides onto a base screen-theme object.
 * Returns the base object unchanged when there are no overrides for the screen.
 */
export function applyClientOverride<T extends Record<string, string>>(
  base: T,
  overrides: Record<string, string> | undefined,
): T {
  if (!overrides) return base;
  return { ...base, ...overrides } as T;
}